"use client";

import { createContext, type ReactNode, useContext, useMemo, useRef } from "react";

interface RevealMemory {
  hasRevealed: (key: string) => boolean;
  markRevealed: (key: string) => void;
}

const NO_MEMORY: RevealMemory = {
  hasRevealed: () => false,
  markRevealed: () => {},
};

const RevealMemoryContext = createContext<RevealMemory>(NO_MEMORY);

/**
 * Remembers which pages have already played their reveal, outside the page
 * components themselves. The viewer mounts only a window of pages, so a page
 * scrolled far enough away is unmounted and its own "already revealed" latch
 * dies with it — without this store, scrolling back replays every animation.
 */
export function RevealMemoryProvider({ children }: { children: ReactNode }) {
  const seen = useRef<Set<string>>(new Set());
  const value = useMemo<RevealMemory>(
    () => ({
      hasRevealed: (key) => seen.current.has(key),
      markRevealed: (key) => {
        seen.current.add(key);
      },
    }),
    [],
  );

  return (
    <RevealMemoryContext.Provider value={value}>
      {children}
    </RevealMemoryContext.Provider>
  );
}

/** Forgets everything by default, so surfaces without a provider keep replaying. */
export function useRevealMemory() {
  return useContext(RevealMemoryContext);
}
